// src/components/sensors/SensorLiveUi.js
// FlowSense sensor tables — live status + FlowSense AI UI pieces — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
//   LiveStatusIndicator  dot + label per row. A one-shot CSS ping plays when a
//                        new reading lands (keyed on the arrival count) — no
//                        infinite per-row animations.
//   LiveFeedBanner       dashboard WebSocket state, kept separate from sensor state.
//   SensorAiBadge        compact FlowSense AI cell; details open in a lazy popover.
//   SensorAiPanel        the full AI read-out (flood, asset health, edge anomaly).
//   AiFleetStrip         fleet-level AI counts that double as a filter.
// ═══════════════════════════════════════════════════════════════════════════
import React, { memo } from 'react';
import {
  Alert, AlertDescription, Badge, Box, Button, Divider, Flex, HStack, Icon,
  Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal, Progress, Text,
  Tooltip, VStack, useColorModeValue,
} from '@chakra-ui/react';
// Chakra v2 peer dependency (Chakra 2.10 no longer re-exports keyframes)
import { keyframes } from '@emotion/react';
import {
  MdAutoAwesome, MdCloudOff, MdHealthAndSafety, MdInsights, MdSensors, MdShowChart, MdWaterDrop,
} from 'react-icons/md';
import { LIVE_STATE_META, describeLiveState, formatAgo } from './sensorLiveStatus';
import { AI_LEVEL_META } from './sensorAiInsights';

const ping = keyframes`
  0%   { transform: scale(1);   opacity: 0.75; }
  100% { transform: scale(2.8); opacity: 0; }
`;
const breathe = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
`;
const REDUCED = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

/**
 * @param {object} p.row         registry row (liveState, arrivals, lastHeardMs, …)
 * @param {object} p.thresholds  registry thresholds
 * @param {string} [p.ageLabel]  formatAgo() of the row — only used to re-render when the age text changes
 * @param {boolean} [p.showLabel]
 */
// eslint-disable-next-line no-unused-vars
export const LiveStatusIndicator = memo(({ row, thresholds, ageLabel, showLabel = true, size = '9px' }) => {
  const meta = LIVE_STATE_META[row.liveState] || LIVE_STATE_META.no_data;
  const color = `${meta.color}.400`;
  const transmitting = row.liveState === 'transmitting';
  return (
    <Tooltip label={describeLiveState(row, thresholds, Date.now())} hasArrow openDelay={150}>
      <HStack spacing={1.5} display="inline-flex" aria-label={`Sensor ${meta.label}`} role="status">
        <Box position="relative" w={size} h={size} flexShrink={0}>
          <Box
            w={size} h={size} borderRadius="full" bg={color}
            boxShadow={transmitting ? '0 0 0 3px rgba(72, 187, 120, 0.25)' : undefined}
          />
          {row.arrivals > 0 && (
            // key = arrival count → the ping restarts once per new reading, then stops
            <Box
              key={row.arrivals}
              position="absolute" inset={0} borderRadius="full" bg={color}
              animation={`${ping} 1.1s ease-out 1 forwards`}
              pointerEvents="none"
              sx={REDUCED}
            />
          )}
        </Box>
        {showLabel && (
          <Text fontSize="10px" fontWeight="800" letterSpacing="0.05em" color={`${meta.color}.500`} whiteSpace="nowrap">
            {meta.short}
          </Text>
        )}
      </HStack>
    </Tooltip>
  );
});
LiveStatusIndicator.displayName = 'LiveStatusIndicator';

/**
 * WebSocket state for the whole table. A dropped feed never marks sensors offline;
 * it only tells the operator that statuses are frozen at the last data received.
 */
export const LiveFeedBanner = memo(({ isConnected, counts, lastArrivalMs, now }) => {
  const okBg = useColorModeValue('green.50', 'rgba(72, 187, 120, 0.10)');
  const okBorder = useColorModeValue('green.100', 'rgba(72, 187, 120, 0.25)');
  if (!isConnected) {
    return (
      <Alert status="warning" variant="left-accent" borderRadius="lg" mb={3} py={2} fontSize="sm">
        <Icon as={MdCloudOff} boxSize={5} mr={3} color="orange.400" />
        <AlertDescription>
          Live feed disconnected — reconnecting. Showing the last data received
          {lastArrivalMs ? ` (${formatAgo(lastArrivalMs, now)})` : ''}; sensor statuses resume updating when the feed is back.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Flex
      align="center" gap={3} mb={3} px={3} py={1.5} borderRadius="lg" borderWidth="1px"
      bg={okBg} borderColor={okBorder} fontSize="xs" flexWrap="wrap" role="status" aria-live="polite"
    >
      <HStack spacing={1.5}>
        <Box w="7px" h="7px" borderRadius="full" bg="green.400" animation={`${breathe} 2.4s ease-in-out infinite`} sx={REDUCED} />
        <Text fontWeight="800" color="green.600" letterSpacing="0.05em">LIVE FEED</Text>
      </HStack>
      <Text color="gray.500">
        <b>{counts.transmitting}</b> transmitting · <b>{counts.online}</b> online · <b>{counts.offline}</b> offline
        {counts.unregistered ? <> · <b>{counts.unregistered}</b> not in register</> : null}
      </Text>
      {lastArrivalMs && <Text color="gray.400" ml="auto">last reading {formatAgo(lastArrivalMs, now)}</Text>}
    </Flex>
  );
});
LiveFeedBanner.displayName = 'LiveFeedBanner';

const pct = (p) => `${Math.round(p * 100)}%`;

/** Full AI read-out for one sensor. */
export const SensorAiPanel = memo(({ ai, kind }) => {
  const sub = useColorModeValue('gray.500', 'gray.400');
  const { flood, health, anomaly, reasons } = ai;
  return (
    <VStack align="stretch" spacing={3} fontSize="xs">
      <HStack spacing={1.5}>
        <Icon as={MdAutoAwesome} color="purple.400" />
        <Text fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" fontSize="10px">FlowSense AI</Text>
        <Badge colorScheme={AI_LEVEL_META[ai.level].color} fontSize="9px" ml="auto">{AI_LEVEL_META[ai.level].label}</Badge>
      </HStack>

      {kind === 'water_level' && (
        <Box>
          <HStack spacing={1.5} mb={1}>
            <Icon as={MdWaterDrop} color="cyan.500" />
            <Text fontWeight="700">Flood forecast</Text>
          </HStack>
          {flood ? (
            <VStack align="start" spacing={1}>
              <HStack spacing={2}>
                <Text fontSize="lg" fontWeight="800" color={flood.probability >= 0.65 ? 'purple.500' : undefined}>{pct(flood.probability)}</Text>
                <Text color={sub}>flood risk</Text>
                {flood.severity && <Badge colorScheme="purple" variant="subtle" fontSize="9px">{String(flood.severity).toUpperCase()}</Badge>}
              </HStack>
              <Progress value={flood.probability * 100} size="xs" colorScheme="purple" borderRadius="full" w="100%" />
              {flood.minutesToOvertop !== null && (
                <Text>Est. overtopping: <b>{flood.minutesToOvertop <= 0 ? 'now' : `~${Math.round(flood.minutesToOvertop)} min`}</b></Text>
              )}
              {flood.ratePerMin !== null && <Text color={sub}>Rate of rise {flood.ratePerMin.toFixed(2)} / min</Text>}
              <Text color={sub}>
                {flood.source}
                {flood.confidence !== null ? ` · confidence ${pct(flood.confidence > 1 ? flood.confidence / 100 : flood.confidence)}` : ''}
                {flood.stale ? ' · server prediction is stale' : ''}
              </Text>
            </VStack>
          ) : <Text color={sub}>No flood prediction.</Text>}
        </Box>
      )}

      {(health || kind === 'pressure') && (
        <Box>
          <HStack spacing={1.5} mb={1}>
            <Icon as={MdHealthAndSafety} color="teal.500" />
            <Text fontWeight="700">Linked asset health</Text>
          </HStack>
          {health ? (
            <HStack spacing={3} flexWrap="wrap">
              {health.score !== null && <Text><b>{Math.round(health.score)}</b><Text as="span" color={sub}>/100</Text></Text>}
              {health.status && <Badge colorScheme={health.level === 'critical' ? 'red' : health.level === 'warning' ? 'orange' : 'green'} fontSize="9px">{health.status}</Badge>}
              {health.rulDays !== null && <Text color={sub}>RUL <b>{Math.round(health.rulDays)} d</b></Text>}
            </HStack>
          ) : <Text color={sub}>No asset health prediction.</Text>}
        </Box>
      )}

      <Box>
        <HStack spacing={1.5} mb={1}>
          <Icon as={MdShowChart} color="orange.400" />
          <Text fontWeight="700">Edge anomaly check</Text>
        </HStack>
        {anomaly ? (
          <VStack align="start" spacing={0.5}>
            <Badge colorScheme={anomaly.severity === 'critical' ? 'red' : 'orange'} fontSize="9px">{anomaly.label}</Badge>
            <Text>{anomaly.detail}</Text>
            <Text color={sub}>{anomaly.method}</Text>
          </VStack>
        ) : <Text color={sub}>No anomaly in the readings received.</Text>}
      </Box>

      {reasons.length > 0 && (
        <>
          <Divider />
          <VStack align="start" spacing={0.5}>
            {reasons.map((r) => <Text key={r} color={sub}>• {r}</Text>)}
          </VStack>
        </>
      )}
    </VStack>
  );
});
SensorAiPanel.displayName = 'SensorAiPanel';

/** Compact AI cell. Popover content mounts only when opened (isLazy). */
export const SensorAiBadge = memo(({ ai, kind, isOnline }) => {
  const meta = AI_LEVEL_META[ai.level] || AI_LEVEL_META.none;
  const mainColor = ai.flood
    ? (ai.flood.probability >= 0.65 ? 'purple' : ai.flood.probability >= 0.35 ? 'orange' : 'green')
    : ai.health
      ? (ai.health.level === 'critical' ? 'red' : ai.health.level === 'warning' ? 'orange' : 'green')
      : meta.color;
  const main = ai.flood
    ? `${pct(ai.flood.probability)}`
    : ai.health && ai.health.score !== null
      ? `${Math.round(ai.health.score)}`
      : meta.label;
  return (
    <Popover isLazy placement="left" trigger="click">
      <PopoverTrigger>
        <Button
          size="xs" variant="ghost" px={1.5} h="auto" py={1} borderRadius="md"
          onClick={(e) => e.stopPropagation()}
          aria-label={`FlowSense AI: ${meta.label}`}
          opacity={isOnline || ai.hasAny ? 1 : 0.7}
        >
          <VStack spacing={0.5} align="start">
            <HStack spacing={1}>
              <Icon as={ai.flood ? MdWaterDrop : ai.health ? MdHealthAndSafety : MdInsights} boxSize={3} color={`${mainColor}.400`} />
              <Text fontSize="10px" fontWeight="800" color={ai.level === 'none' ? 'gray.400' : `${mainColor}.500`}>
                {main}{ai.flood ? ' risk' : ai.health && ai.health.score !== null ? ' health' : ''}
              </Text>
            </HStack>
            {ai.anomaly && (
              <Badge colorScheme={ai.anomaly.severity === 'critical' ? 'red' : 'orange'} fontSize="8px" variant="solid" borderRadius="full" px={1.5}>
                {ai.anomaly.label}
              </Badge>
            )}
            {ai.flood && ai.flood.minutesToOvertop !== null && ai.flood.probability >= 0.65 && (
              <Text fontSize="9px" color="purple.500" fontWeight="700">
                {ai.flood.minutesToOvertop <= 0 ? 'overtopping now' : `~${Math.round(ai.flood.minutesToOvertop)} min to overtop`}
              </Text>
            )}
          </VStack>
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="300px" onClick={(e) => e.stopPropagation()}>
          <PopoverArrow />
          <PopoverBody p={4}><SensorAiPanel ai={ai} kind={kind} /></PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
});
SensorAiBadge.displayName = 'SensorAiBadge';

/** Fleet AI counts. Clicking toggles the "AI flagged" filter when onToggle is given. */
export const AiFleetStrip = memo(({ summary, active, onToggle }) => {
  const bg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.10)');
  const border = useColorModeValue('purple.100', 'rgba(128, 90, 213, 0.3)');
  if (!summary || !summary.withAi) return null;
  return (
    <Flex
      as={onToggle ? 'button' : 'div'} onClick={onToggle} aria-pressed={onToggle ? !!active : undefined}
      w="100%" textAlign="left" align="center" gap={3} mb={3} px={3} py={2} borderRadius="lg" borderWidth="1px"
      bg={bg} borderColor={active ? 'purple.400' : border} fontSize="xs" flexWrap="wrap"
    >
      <HStack spacing={1.5}>
        <Icon as={MdAutoAwesome} color="purple.400" />
        <Text fontWeight="800" color="purple.500" letterSpacing="0.05em">FLOWSENSE AI</Text>
      </HStack>
      <Text color="gray.500">
        <b>{summary.critical}</b> high risk · <b>{summary.warning}</b> watch · <b>{summary.anomalies}</b> anomal{summary.anomalies === 1 ? 'y' : 'ies'}
        {summary.maxFlood !== null ? <> · peak flood risk <b>{pct(summary.maxFlood)}</b></> : null}
      </Text>
      {onToggle && (
        <HStack spacing={1} ml="auto" color="purple.500" fontWeight="700">
          <Icon as={MdSensors} />
          <Text>{active ? 'Showing AI flagged — click to clear' : 'Show AI flagged'}</Text>
        </HStack>
      )}
    </Flex>
  );
});
AiFleetStrip.displayName = 'AiFleetStrip';

/** Fleet AI summary from per-row AI view-models. */
export const summarizeAi = (aiList) => {
  const s = { withAi: 0, critical: 0, warning: 0, anomalies: 0, flagged: 0, maxFlood: null };
  aiList.forEach((ai) => {
    if (!ai) return;
    if (ai.hasAny) s.withAi += 1;
    if (ai.level === 'critical') s.critical += 1;
    if (ai.level === 'warning') s.warning += 1;
    if (ai.anomaly) s.anomalies += 1;
    if (ai.flagged) s.flagged += 1;
    if (ai.flood && (s.maxFlood === null || ai.flood.probability > s.maxFlood)) s.maxFlood = ai.flood.probability;
  });
  return s;
};
